import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Box, Grid, Typography } from '@mui/material'
import { RestartAlt } from '@mui/icons-material'

import ear from 'assets/svg/ear.svg'
import eye from 'assets/svg/eye.svg'
import pencil from 'assets/svg/pencil.svg'
import tools from 'assets/svg/tools.svg'

import LearningStylePieChart from './LearningStylePieChart'
import { usePUT_resetLearningStyleMutation } from 'redux/api/studentsApi'

export interface IProps {
  data: any
  reset_id?: number
}
const LearningStyleResult: FC<IProps> = ({ data, reset_id }) => {
  const { t } = useTranslation()

  const chartData = data?.categories.map((category) => {
    return {
      name: category.title,
      value: category.percentage,
      fill: category.color_hex_code,
    }
  })
  let predominantStyle = data.categories.reduce((max, category) =>
    max.percentage > category.percentage ? max : category,
  )

  const [
    resetLearningStyle,
    { isLoading: resetLoading, isSuccess: resetSuccess, error: resetError, data: resetResponseData },
  ] = usePUT_resetLearningStyleMutation() 
  

  return (
    <Grid
      container
      sx={{ display: 'flex', width: '100%' }}
    >
      <Grid
        item
        xs={12}
        lg={6}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2rem' }}
      >
        <Typography variant='h4' sx={{ textAlign: 'center', marginBottom: '1rem' }}>
          {' '}
          {reset_id ?  'Learning styles' : `${t('student.your-learning-style')}`}
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography>{`${data.categories[0].percentage} %`}</Typography>
            <Box  className="transparent-background"
              component='img'
              sx={{
                height: '4rem',
                width: '4rem',
                padding: '0.5rem',
                margin: '1rem',
                backgroundColor: `${data.categories[0].color_hex_code}`,
                borderRadius: '50%',
              }}
              alt='icon'
              src={eye}
            />
            <Typography>{`${data.categories[0].title}`}</Typography>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography>{`${data.categories[1].percentage} %`}</Typography>
            <Box  className="transparent-background"
              component='img'
              sx={{
                height: '4rem',
                width: '4rem',
                padding: '0.5rem',
                margin: '1rem',
                backgroundColor: `${data.categories[1].color_hex_code}`,
                borderRadius: '50%',
              }}
              alt='icon'
              src={ear}
            />
            <Typography>{`${data.categories[1].title}`}</Typography>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography>{`${data.categories[2].percentage} %`}</Typography>
            <Box className="transparent-background"
              component='img'
              sx={{
                height: '4rem',
                width: '4rem',
                padding: '0.5rem',
                margin: '1rem',
                backgroundColor: `${data.categories[2].color_hex_code}`,
                borderRadius: '50%',
              }}
              alt='icon'
              src={pencil}
            />
            <Typography>{`${data.categories[2].title}`}</Typography>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography>{`${data.categories[3].percentage} %`}</Typography>
            <Box  className="transparent-background"
              component='img'
              sx={{
                height: '4rem',
                width: '4rem',
                padding: '0.5rem',
                margin: '1rem',
                backgroundColor: `${data.categories[3].color_hex_code}`,
                borderRadius: '50%',
              }}
              alt='icon'
              src={tools}
            />
            <Typography>{`${data.categories[3].title}`}</Typography>
          </Box>
          <LearningStylePieChart chartData={chartData} width={390} />
        </Box>
      </Grid>
      <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', marginTop: '2rem' }}>
        <Typography variant='h4' sx={{ textAlign: 'center', marginBottom: '1rem' }}>
          {' '}
          {`${t('student.predominant-style')}: ${predominantStyle?.title} `}
        </Typography>
        <Typography
          dangerouslySetInnerHTML={{
            __html: predominantStyle?.text,
          }}
        />

        { reset_id && <Button
                variant='contained'
                color='primary'
                onClick={() =>  resetLearningStyle(reset_id.toString()) }
                sx={{ marginTop: '20px', minHeight: '2.5rem', width: '10rem' }}
              >
                <RestartAlt style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.reset')}`}
              </Button> }
      </Grid>
    </Grid>
  )
}

export default LearningStyleResult
